import type OlMap from "ol/Map";
import Overlay from "ol/Overlay";
import type { Options as OverlayOptions } from "ol/Overlay";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const DEFAULT_OFFSET: NonNullable<OverlayOptions["offset"]> = [0, 0];

export interface MapOverlayProps extends Pick<
  OverlayOptions,
  "insertFirst" | "offset" | "position" | "positioning" | "stopEvent"
> {
  map: OlMap;
  children?: React.ReactNode;
}

export function MapOverlay({
  map,
  children,
  insertFirst = false,
  offset = DEFAULT_OFFSET,
  position,
  positioning = "bottom-center",
  stopEvent = true,
}: MapOverlayProps) {
  const [overlay, setOverlay] = useState<Overlay | null>(null);
  const initialOptions = useRef({
    insertFirst,
    offset,
    position,
    positioning,
    stopEvent,
  });

  useEffect(() => {
    const element = document.createElement("div");
    const createdOverlay = new Overlay({
      element,
      ...initialOptions.current,
    });

    setOverlay(createdOverlay);
    map.addOverlay(createdOverlay);

    return () => {
      map.removeOverlay(createdOverlay);
    };
  }, [map]);

  const element = useMemo(() => overlay?.getElement(), [overlay]);

  useEffect(() => {
    overlay?.setPosition(position);
  }, [overlay, position]);

  useEffect(() => {
    overlay?.setOffset(offset);
  }, [offset, overlay]);

  useEffect(() => {
    overlay?.setPositioning(positioning);
  }, [overlay, positioning]);

  if (!element) return null;

  return createPortal(children, element);
}
